import type { AppliedModifier } from "~/types";

/**
 * THE CONTRACT
 *
 * THE BACKEND MUST NEVER MULTIPLY. If you are about to add a `modifier_quantity_semantics` flag
 * (or any other backend-side line multiplier), STOP AND READ THIS FIRST. Site and hub already send
 * LINE-SCALED values, so a backend multiplier overcharges by the line quantity — 50x on a 50-unit
 * line. A branch that gated exactly such a multiplier behind `modifier_quantity_semantics: "picked"`
 * was dismissed for this reason; see docs/decisions.md 2026-08-05.
 *
 * `appliedModifiers[].quantity` on a cart item — and on a stored `OrderProduct.modifiers` row — is
 * the total portion count across the WHOLE LINE, i.e. picked x line quantity. The backend
 * multiplies nothing: `processItemsOrder` charges `price * (quantity || 1)`, as it always has.
 *
 * A `split_quantity` group is NEVER scaled. Its shares already sum to the line quantity
 * (`validateSplitQuantityModifiers` enforces that server-side), so multiplying again would charge
 * the add-on once per unit instead of once per line.
 *
 * WHY THE SCALING LIVES HERE AND NOT IN `addToCart`
 *
 * Because `addToCart` cannot tell its callers apart. The product modals hand it PICKED quantities
 * straight off `<OptionList>`; `ShoppingCart/CartSummary.vue`'s quantity operator hands it an
 * existing cart item's modifiers, which are ALREADY scaled. Multiplying inside `addToCart` would
 * scale the latter a second time on every quantity change. So `addToCart` stays a passthrough and
 * each caller declares what it holds: modals call `scaleModifiersToLine`, the operator calls
 * `rescaleModifiersToLine`, and a picker re-seeded from already-scaled state calls
 * `descaleModifiersFromLine`.
 *
 * `split_quantity` IS TRISTATE, AND ABSENT IS NOT `false`
 *
 * `modifierGroups.split_quantity` is nullable in the DB, so every picker normalises it with
 * `=== true` before it reaches a cart item. That makes an ABSENT flag mean exactly one thing: the
 * row was hydrated from a stored order, where the backend does not persist the flag at all. Absent
 * is therefore UNKNOWN, never "non-split" — and because scaling a split share is the expensive
 * direction (it charges an add-on once per unit instead of once per line), an unknown row is left
 * alone rather than guessed at.
 *
 * Consumers that need the real flag for such a row must read it from the GROUP, not the entry:
 * `clampSplitQuantityModifiers` and the required-group gates both take the group list for exactly
 * this reason. `modifier_group_id` IS persisted, so group membership is always recoverable.
 *
 * A STORED ROW DOES NOT RESCALE UNTIL ITS FLAG IS RESOLVED
 *
 * Backend `main` persists no `split_quantity` snapshot, and `getOrder` does not include
 * `product.modifierGroups`, so a modifier hydrated from a stored order arrives with the flag ABSENT.
 * Both `rescaleModifiersToLine` and `descaleModifiersFromLine` short-circuit on
 * `split_quantity !== false`, so until something resolves the flag they are a no-op on that row.
 *
 * On `orders/[id]/edit.vue` the flag is supplied per GROUP, not per line: `updateModifiers` rebuilds
 * only the group the customer just touched, from options carrying `split_quantity === true`. So a
 * group they re-pick rescales with the line, while a stored group they never open stays absent and
 * does not. Unlike hub, there is no whole-line enrichment step.
 *
 * Leaving it absent by default is deliberate: unknown skips rescaling, which undercharges rather than
 * overcharging a split share once per unit. Resolve it from the GROUP when the group list is in scope;
 * never default it to `false`.
 *
 * WHAT THIS FIXED
 *
 * Ticket 870 was two defects against this contract, both in `addToCart`:
 *
 *   quantity: isBundle || mod.split_quantity ? mod.quantity : payload.quantity
 *
 * Bundles were exempted from the scaling, so a £2.50 add-on on a 50-unit bundle line charged £2.50
 * instead of £125. And the non-split branch wrote the line quantity *over* the picked value instead
 * of multiplying by it, so a modifier picked at 3 on a 10-unit line charged 10 portions rather than
 * 30 — while the product modal's own total said 30.
 *
 * DUPLICATION: `scaleModifiersToLine`, `rescaleModifiersToLine` and `descaleModifiersFromLine` are
 * duplicated in `hub/utils/lineModifierQuantities.ts`. `shared/` is not wired into hub, so those three
 * must be kept in lockstep by hand — change one, change the other. They may differ only in import and
 * typing style; never in behaviour.
 *
 * `modifiersLineTotal` and `storedModifiersForCart` are site-only and must STAY site-only:
 *   - `modifiersLineTotal` applies a line multiplier, which is correct here because site holds PICKED
 *     counts in modal state. hub holds LINE TOTALS, so the same multiplier there would charge a
 *     non-split add-on quantity x quantity. hub's `modifiersPrice` computeds are a plain sum by design.
 *   - `storedModifiersForCart` drops deselected rows on the way IN; hub does the same job on the way
 *     OUT via `utils/outboundModifiers.ts`. Same rule, different boundary.
 * Do not "restore parity" by porting either one to hub.
 *
 * `app/` does NOT have this util. It is an open CLAUDE.md §4a parallel gap, not an exemption: app
 * shares the same `x-access-token-customer` contract as site, so it undercharges multi-unit lines
 * exactly as site did before ticket 870. Port this file — or lift it into `shared/`, which site and
 * app both already consume, and which would remove this hand-sync note for the site/app pair.
 */

const pickedToLine = (mod: AppliedModifier, lineQuantity: number): number =>
  mod.split_quantity === true ? (mod.quantity ?? 1) : (mod.quantity ?? 1) * lineQuantity;

/**
 * Whole-line money contribution of a modal's `selectedModifiers`, in whatever unit `priceOf` returns.
 *
 * THE LINE MULTIPLIER BELONGS HERE, and it applies to only one kind of modifier:
 *   - `split_quantity === false` — a picked count that applies to every unit, so it scales with the
 *     line.
 *   - anything else — already line-relative and must NOT be scaled. A split share sums to the line
 *     amount by definition, and a row hydrated from a stored order carries a line total with no flag at
 *     all (absent is UNKNOWN — see the tristate note above, and note this is the direction that costs
 *     the customer money if guessed wrong).
 *
 * All three product modals used to scale the whole per-unit figure instead
 * (`(base / 100 + modifiersPrice) * modalQuantity`), which multiplied a split share a second time: base
 * 850p at qty 3 with shares A=2 (150p) + B=1 (0p) displayed £34.50 against £28.50 charged, and the
 * overstatement grew linearly with the line. hub's shape was already correct because hub holds LINE
 * TOTALS in modal state and needs no multiplier at all; site holds PICKED counts, which is why the
 * multiplier exists here and not there.
 *
 * `priceOf` is a parameter because the pages disagree on units and on which price field they read:
 * `store/[slug].vue` and `orders/[id]/edit.vue` price on `price_gross`, `premium/store/[slug].vue`
 * prices its base on `price_net` but its modifiers on `price_gross` (pre-existing, recorded there).
 *
 * PER-UNIT is this figure divided by the line quantity — that identity is exact for both branches
 * (`price * picked * line / line = price * picked`; `price * share / line`), so a caller that needs a
 * per-unit contribution divides rather than reimplementing the rule.
 */
export function modifiersLineTotal<T extends { quantity?: number; split_quantity?: boolean }>(
  modifiers: T[] | undefined | null,
  lineQuantity: number,
  priceOf: (mod: T) => number,
): number {
  const line = Number(lineQuantity) > 0 ? Number(lineQuantity) : 1;
  return (modifiers || []).reduce((sum, mod) => {
    const contribution = priceOf(mod) * Number(mod.quantity || 0);
    return sum + (mod.split_quantity === false ? contribution * line : contribution);
  }, 0);
}

/**
 * Stored `OrderProduct.modifiers` -> cart-ready rows. Call this at EVERY point a stored order's
 * modifiers enter cart state.
 *
 * DESELECTED OPTIONS ARE DROPPED, NEVER CARRIED AT QUANTITY 0. `processItemsOrder` charges
 * `price * (mod.quantity || 1)`, so a forwarded `0` is promoted server-side to one charged portion —
 * an add-on nobody ordered. The old picker persisted unticked options at `quantity: 0`, so stored
 * orders really do contain them (`docs/decisions.md` records 34 rows across 24 staging orders), and
 * `compareSets` rejects on length mismatch, so carrying one also forks the customer's cart into two
 * lines for one dish.
 *
 * This exists because the filter was inlined at two of the four hydration paths and merely commented
 * at the other two (`premium/store/[slug].vue`). A helper wired at some sites and not others is not
 * coverage — see the same rule in `hub/utils/outboundModifiers.ts`.
 *
 * `?? 1` not `|| 1`: a MISSING quantity legitimately means one portion for the line and must survive,
 * while an explicit `0` must not.
 *
 * Quantities are passed through UNSCALED — a stored row is already a line total.
 */
export function storedModifiersForCart<T extends { quantity?: number }>(
  modifiers: T[] | undefined | null,
): T[] {
  return (modifiers || []).filter((m) => (m.quantity ?? 1) > 0);
}

/**
 * Picked quantities -> line totals. Call this wherever a product modal's `selectedModifiers` is
 * about to become a cart item.
 *
 * Returns `[]` on a non-positive or non-finite line quantity rather than echoing the input back:
 * under this contract a caller reads whatever it gets as a LINE TOTAL, so handing back picked
 * values would silently understate the line. A zero-quantity line has no modifiers by definition,
 * and every caller clamps to `min_order_amount` (or routes to `removeFromCart`) before reaching
 * here, so this is a guard rather than a live path.
 */
export function scaleModifiersToLine(
  modifiers: AppliedModifier[] | undefined | null,
  lineQuantity: number,
): AppliedModifier[] {
  if (!modifiers || !modifiers.length) return [];
  const line = Number(lineQuantity);
  if (!Number.isFinite(line) || line <= 0) return [];

  return modifiers.map((mod) => ({ ...mod, quantity: pickedToLine(mod, line) }));
}

/**
 * Line totals -> line totals against a NEW line quantity. Used by the cart row's quantity operator,
 * which owns already-scaled modifiers and a changed line quantity.
 *
 * Recovers the picked count by dividing out the old line quantity. That division is exact for
 * anything this app wrote, since it only ever writes `picked * lineQuantity`. Rows written before
 * ticket 870 stored the bare line quantity (the picked count was discarded at write time), so they
 * divide to 1 — the correct answer for a checkbox modifier and the only defensible reading for an
 * operator one, because the original pick is not recoverable from the stored row.
 *
 * Only a KNOWN non-split modifier (`split_quantity === false`) is rescaled:
 *   - `true`  — a share is already line-relative; rebalancing it is `clampSplitQuantityModifiers`'
 *               job, and multiplying it would charge the add-on once per unit.
 *   - absent  — UNKNOWN (a stored-order row, see the header). Left untouched, because guessing
 *               "non-split" is the direction that costs the customer money.
 *
 * The result need NOT be a multiple of the new line quantity (stored 50, old line 3, new line 4 ->
 * 67). That is a legitimate line total and the backend charges it correctly. Per-unit displays must
 * therefore not treat "does not divide evenly" as "this is a per-unit pick" — see hub's
 * `AdHocItemRow.modifiersUnitNetPence`, which amortises any quantity larger than the line and keeps
 * the per-unit reading only for a genuinely smaller legacy value.
 */
export function rescaleModifiersToLine(
  modifiers: AppliedModifier[] | undefined | null,
  oldLineQuantity: number,
  newLineQuantity: number,
): AppliedModifier[] {
  if (!modifiers || !modifiers.length) return [];
  const oldLine = Number(oldLineQuantity);
  const newLine = Number(newLineQuantity);
  if (!Number.isFinite(oldLine) || oldLine <= 0) return [...modifiers];
  if (!Number.isFinite(newLine) || newLine <= 0) return [...modifiers];
  if (oldLine === newLine) return [...modifiers];

  return modifiers.map((mod) => {
    if (mod.split_quantity !== false) return { ...mod };
    const stored = Number(mod.quantity ?? 1);
    if (!Number.isFinite(stored) || stored === 0) return { ...mod };
    return { ...mod, quantity: Math.max(1, Math.round((stored / oldLine) * newLine)) };
  });
}

/**
 * Line totals -> picked quantities. The inverse of `pickedToLine`, for re-seeding a picker from
 * state that is already scaled.
 *
 * `<OptionList>` copies the seeded quantity into its own `selectedList` and derives the remaining
 * allowance from it (`updatedLimit = limit - sum(selectedList.quantity)`). Seeding a LINE TOTAL
 * there drives that allowance negative — a 3-platter split group seeded with 6 + 3 yields -6 — and
 * `QuantityOperator` then renders a negative upper bound, making the share unusable.
 *
 * A split share is passed through undivided: it is already line-relative, so dividing it would
 * understate the pick. An unknown flag is passed through for the same reason
 * `rescaleModifiersToLine` leaves it alone.
 */
export function descaleModifiersFromLine(
  modifiers: AppliedModifier[] | undefined | null,
  lineQuantity: number,
): AppliedModifier[] {
  if (!modifiers || !modifiers.length) return [];
  const line = Number(lineQuantity);
  if (!Number.isFinite(line) || line <= 0) return [...modifiers];

  return modifiers.map((mod) => {
    if (mod.split_quantity !== false) return { ...mod };
    const stored = Number(mod.quantity ?? 1);
    if (!Number.isFinite(stored) || stored === 0) return { ...mod };
    return { ...mod, quantity: Math.max(1, Math.round(stored / line)) };
  });
}
